import { Button } from '@/components/ui/button';
import { type SeverityType } from '@kirin/api-client/contracts/shared/severity';
import { type JSX, useState, useMemo } from 'react';
import {
  PolicyPatternModal,
  type NormalizedPattern,
} from '@/components/PolicyPatternModal';
import { getPatternModalValidation } from '../helpers/patternModalRegistry';

/** Stored rule shape — legacy policies use `reason`, newer ones use `description`. */
interface StoredPattern {
  pattern: string;
  reason?: string;
  description?: string;
  severity: SeverityType;
}

interface PatternArrayModalFieldProps {
  value: StoredPattern[];
  onChange: (value: StoredPattern[]) => void;
  disabled?: boolean;
  /** View-only: open modal and read rules; no add/edit/remove. */
  readOnly?: boolean;
  /** Support role: rows stay viewable/editable, only Edit-submit/Delete are disabled. */
  isSupportReadOnly?: boolean;
  policyKey?: string;
  policyName: string;
  policyDescription: string;
  baseline: StoredPattern[];
}

export function PatternArrayModalField({
  value,
  onChange,
  disabled = false,
  readOnly = false,
  isSupportReadOnly = false,
  policyKey,
  policyName,
  policyDescription,
  baseline,
}: PatternArrayModalFieldProps): JSX.Element {
  const [modalOpen, setModalOpen] = useState(false);

  // Policies using 'description': secret-detection, cursor-database.
  // Policies using 'reason': extension-security, mcp-security.
  const usesDescriptionField =
    policyKey === 'secret-detection' ||
    policyKey === 'cursor-database' ||
    policyKey === 'pii-detection';

  // Calculate changes by comparing actual rules, not just lengths.
  const changes = useMemo(() => {
    const added = value.filter(
      v => !baseline.some(b => b.pattern === v.pattern)
    ).length;
    const removed = baseline.filter(
      b => !value.some(v => v.pattern === b.pattern)
    ).length;
    return { added, removed };
  }, [value, baseline]);

  const hasChanges = changes.added > 0 || changes.removed > 0;

  const validation = policyKey
    ? getPatternModalValidation(policyKey)
    : undefined;

  // Rules the policy's check says can never match, counted here so they show
  // without opening the modal. They do not block saving: the backend refuses
  // only rules being added (KIR-4928), so a stored one stays until fixed.
  const neverMatchingCount = useMemo(
    () =>
      validation
        ? value.filter(v => validation.validatePattern(v.pattern) !== undefined)
            .length
        : 0,
    [value, validation]
  );

  // Stored shape → normalized shape consumed by the modal.
  const normalizedPatterns: NormalizedPattern[] = useMemo(
    () =>
      value.map(v => ({
        pattern: v.pattern,
        description: v.description ?? v.reason ?? '',
        severity: v.severity,
      })),
    [value]
  );

  const handlePatternsChange = (next: NormalizedPattern[]): void => {
    const stored: StoredPattern[] = next.map(p =>
      usesDescriptionField
        ? {
            pattern: p.pattern,
            description: p.description,
            severity: p.severity,
          }
        : { pattern: p.pattern, reason: p.description, severity: p.severity }
    );
    onChange(stored);
  };

  return (
    <div className="space-y-3">
      {/* Description text */}
      <p className="text-sm text-muted-foreground">{policyDescription}</p>

      {/* Button to open modal */}
      <Button
        type="button"
        onClick={() => setModalOpen(true)}
        disabled={disabled}
      >
        {readOnly && !disabled
          ? `View Custom Rules (${value.length})`
          : `Edit Custom Rules (${value.length})`}
      </Button>

      {/* Change indicator */}
      {hasChanges && (
        <div className="text-xs space-y-1">
          {changes.added > 0 && (
            <p className="text-muted-foreground">
              {changes.added} rule{changes.added !== 1 ? 's' : ''} added
            </p>
          )}
          {changes.removed > 0 && (
            <p className="text-muted-foreground">
              {changes.removed} rule{changes.removed !== 1 ? 's' : ''} removed
            </p>
          )}
        </div>
      )}

      {neverMatchingCount > 0 && (
        <p className="text-xs text-amber-700 dark:text-amber-300">
          {neverMatchingCount === 1
            ? '1 rule never matches.'
            : `${neverMatchingCount} rules never match.`}
          {/* Only someone who can edit the rules can act on the note. */}
          {!readOnly &&
            !disabled &&
            (neverMatchingCount === 1
              ? ' Open the rules to fix or delete it.'
              : ' Open the rules to fix or delete them.')}
        </p>
      )}

      {/* Modal */}
      <PolicyPatternModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        policyName={policyName}
        policyDescription={policyDescription}
        patterns={normalizedPatterns}
        onPatternsChange={handlePatternsChange}
        disabled={disabled}
        readOnly={readOnly && !disabled}
        isSupportReadOnly={isSupportReadOnly && !disabled}
        {...validation}
      />
    </div>
  );
}
